import { Injectable } from '@angular/core';
import { BehaviorSubject, from, map, Observable, switchMap, take, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { SecureStorageService } from 'src/app/services/secure-storage.service';

const apiURLs = {
  root: environment.apiURL,
  login: '/auth/login',
  loginAs: '/auth/login-as-user/',
  logout: '/auth/logout',
  forgotPswd: '/auth/forgot-password',
  setPswd: '/auth/set-password',
  refreshToken: '/auth/refresh'
};

const TOKEN_KEY = 'token';
const DEPARTMENT_KEY = 'AdminDepartment';

export interface JwtPayload {
  userId: number;
  department?: string;
  partnerProfile?: boolean;
  iat?: number;
  exp?: number;
}

export function decodeToken(token: string | null | undefined): JwtPayload | null {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  isAuthenticated: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  token = '';

  adminDepartment: BehaviorSubject<string> = new BehaviorSubject<string>('');
  partnerProfile: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private secureStorageService: SecureStorageService
  ) { 
    this.loadToken();
    this.loadAdminDepartment();
  }

  async loadToken() {
    const token = await this.secureStorageService.secureGet({ key: TOKEN_KEY + 'Access' });    
    if (token && token.value) {
      console.log('set token: ', token.value);
      this.token = token.value;
      this.setTokenClaims(token.value);
      this.isAuthenticated.next(true);
    } else {
      this.isAuthenticated.next(false);
    }
  }

  async getTokenPayload(): Promise<JwtPayload | null> {
    const token = await this.getToken();
    return decodeToken(token?.value);
  }

  private setTokenClaims(token: string) {
    const info = decodeToken(token);
    this.partnerProfile.next(info?.partnerProfile === true);
  }

  public getOTP(credentials : {email: string, password: string}): Observable<any> {
    return this.http.post(apiURLs.root + apiURLs.login, credentials);
  }

  public login(credentials : {email: string, password: string, code: number}): Observable<any> {
    return this.http.post(apiURLs.root + apiURLs.login, credentials).pipe(
      map((data: any) => data),
      switchMap(data => {
        console.log(data)
        this.setRefreshToken(data.refreshToken);
        this.saveAdminDepartment(data.accessToken);
        return from(this.setToken(data.accessToken));
      }),
      tap(_ => {
        this.isAuthenticated.next(true);
      })
    );
  }

  public loginAs(userId: number): Observable<any> {
    return this.http.get(apiURLs.root + apiURLs.loginAs + userId);
  }

  private saveAdminDepartment(token: string) {
    const info = decodeToken(token);
    if (info?.department) {
      this.adminDepartment.next(info.department);
      this.secureStorageService.secureSet({key: DEPARTMENT_KEY, value: info.department});
    }
  }

  async getAdminDepartment() {
    const department = await this.secureStorageService.secureGet({key: DEPARTMENT_KEY});
    return department;
  }

  async loadAdminDepartment() {
    const department = await this.getAdminDepartment();
    if (department.value) {
      this.adminDepartment.next(department.value)
    } else {
      const token = await this.getToken();
      if (token.value) {
        const info = decodeToken(token.value);
        if (info?.department) {
          this.adminDepartment.next(info.department);
        }
      }
    }
  }

  private logoutFromApi(): Observable<any> {
    return this.http.get(apiURLs.root + apiURLs.logout);
  }

  setToken(accessToken: string) {
    this.setTokenClaims(accessToken);
    return this.secureStorageService.secureSet({key: TOKEN_KEY + 'Access', value: accessToken});
  }

  setRefreshToken(refreshToken: string) {
    return this.secureStorageService.secureSet({key: TOKEN_KEY + 'Refresh', value: refreshToken});
  }

  async getToken() {
    const token = await this.secureStorageService.secureGet({ key: TOKEN_KEY + 'Access' });
    return token; 
  }

  async getRefreshToken() {
    const token = await this.secureStorageService.secureGet({ key: TOKEN_KEY + 'Refresh' });
    return token; 
  }

  public logout(logoutFromApi: boolean = true): Promise<void> {
    this.isAuthenticated.next(false);
    this.partnerProfile.next(false);
    if(logoutFromApi) this.logoutFromApi().pipe(take(1)).subscribe();
    this.secureStorageService.remove({key: 'clientName'});
    return this.secureStorageService.remove({key: TOKEN_KEY + 'Access'});
  }

  public refreshToken(refreshToken: string): Observable<any> {
    return this.http.get(apiURLs.root + apiURLs.refreshToken, {
        headers: {
          Authorization: `Bearer ${refreshToken}`
        }
    });
  }

  saveClientName(name: string) {
    return this.secureStorageService.secureSet({key: 'clientName', value: name});
  }

  async getClientName() {
    const name = await this.secureStorageService.secureGet({ key: 'clientName' });
    return name; 
  }

  public forgotPassword(email: {email: string}): Observable<any> {
    return this.http.post(apiURLs.root + apiURLs.forgotPswd, email);
  }

  public setPassword(data: {token: string | null, newPassword: string}): Observable<any> {
    return this.http.post(apiURLs.root + apiURLs.setPswd, data);
  }
}
